import React, { useCallback, useEffect, useLayoutEffect, useRef } from "react";
import OneMessage from "./SingleMessage";
import "../../../../assets/css/styles.css";
import { useTheme } from "../../../../context/ThemeProvider";
import { scrollToBottom, conditionalAutoScroll } from "../../../../utils/scrollHelpers";
import Loader from "../../../Loader/Loader";
import ForNewUserWhichIsNotColab from "../../../Spectra/ForNewUserWhichIsNotColab";
import ScrollToBottomStore from "../../../../store/ScrollToBottomStore";
import regenerate from "../../../../api/regenerate";
import SparkEmptyState from "../../SparkEmptyState";
import { useSparkContext } from "../../../../context/SparkContext";
import useDesktopDeviceStore from "../../../../store/useDesktopDeviceStore";
import { PERMISSIONS } from "../../../../utils/permissions";

const ChatMessages = ({
  messages,
  chat,
  user,
  orgId,
  deleteMessage,
  editFunctionality,
  regenerateIndex,
  setRegenerateIndex,
  editedIndex,
  setEditedIndex,
  generateStatus,
  setGenerateStatus,
  defaultModel,
  files,
  modeRef,
  setIsLatestMessageLoading,
  messageContainerRef,
  isMessagesRendered,
  setMessagesRendered,
  isDataReady,
  setMessageCount,
  setIsNewDrag,
  isNewDrag,
  pendingApprovals,
  onApprovalConfirm,
  onApprovalCancel,
  onApprovalExpire,
  onApprovalCard,
  onClearApprovals,
  regenerateMessageMutation,
  queryClient,
  isNebulaMode = false,
  compactAnswers = true,
  expandedExchangeIds,
  collapsedExchangeIds,
  onExchangeExpandedChange,
}) => {
  const { chat_id, space_id, space, isNewChatWithSpace } = useSparkContext();
  const { theme } = useTheme();
  const setShowScrollButton = ScrollToBottomStore((state) => state.setShowScrollButton);
  const selectedDeviceId = useDesktopDeviceStore((s) => s.selectedDevices[chat_id] ?? null);

  // Keep refs to values that change every streaming token so useCallback deps stay stable
  const messagesRef = useRef(messages);
  useEffect(() => { messagesRef.current = messages; });
  const chatRef = useRef(chat);
  useEffect(() => { chatRef.current = chat; });

  const regenFunc = useCallback((id, index, modelSelected, styleSelection, styleVersionId) => {
    onClearApprovals?.(id);
    const m = modelSelected ? modelSelected : defaultModel;
    return regenerate({
      id,
      index,
      messages: messagesRef.current,
      regenerateMessageMutation,
      modeRef,
      chat_id,
      orgId,
      user,
      files,
      modelSelected: m,
      setIsLatestMessageLoading,
      setRegenerateIndex,
      setGenerateStatus,
      space,
      chat: chatRef.current,
      onApprovalCard,
      queryClient,
      deviceId: selectedDeviceId,
      styleSelection,
      styleVersionId,
    });
  }, [onClearApprovals, defaultModel, regenerateMessageMutation, modeRef, chat_id, orgId, user, files, setIsLatestMessageLoading, setRegenerateIndex, setGenerateStatus, space, onApprovalCard, queryClient, selectedDeviceId]);

  // Force-scroll to the bottom only when actually switching to a different chat
  // (a fresh view — there's no existing reading position to protect). When the
  // user is already viewing this chat and a new message merely arrives (their
  // own send, a collaborator's message in a Spectra spark, an agent tool-step,
  // etc.), only scroll if they were already at the bottom — otherwise this
  // yanked anyone who had scrolled up to read older messages back down every
  // time messages.length changed.
  const prevChatIdRef = useRef(chat_id);
  useLayoutEffect(() => {
    const isChatSwitch = prevChatIdRef.current !== chat_id;
    prevChatIdRef.current = chat_id;
    // Cleared on unmount/dep-change — without this, rapidly switching chats
    // right after a message count change (within the 10ms window) let this
    // stale timer fire after the switch and call scrollToBottom(), which
    // queries the global #chat-messages/#spark-scroll-container by id — i.e.
    // it force-scrolled the NEWLY mounted chat to the bottom, overriding
    // wherever the user had just scrolled to.
    const timer = setTimeout(() => {
      if (isChatSwitch) {
        scrollToBottom(false);
      } else {
        conditionalAutoScroll(false);
      }
      setMessagesRendered(true);
    }, 10);
    return () => clearTimeout(timer);
  }, [messages.length, chat_id, setMessagesRendered]);

  // Detect scrolling behavior
  useEffect(() => {
    const handleScroll = () => {
      if (messageContainerRef.current) {
        const { scrollHeight, scrollTop, clientHeight } = messageContainerRef.current;
        const isAtBottom = scrollHeight - scrollTop <= clientHeight + 20;
        setShowScrollButton(!isAtBottom);
      }
    };

    const scrollContainer = messageContainerRef.current;
    if (scrollContainer) {
      scrollContainer.addEventListener("scroll", handleScroll);
      handleScroll();
    }
    setMessageCount(messages.length);
    return () => {
      if (scrollContainer) {
        scrollContainer.removeEventListener("scroll", handleScroll);
      }
    };
  }, [messages.length, messageContainerRef, setShowScrollButton, setMessageCount]);

  if (!isDataReady) {
    return (
      <div className="flex flex-1 w-full">
        <Loader variant="chat" />
      </div>
    );
  }

  if (isNewChatWithSpace && !PERMISSIONS.CREATE_NEW_SPARK_IN_SPECTRA(space)) {
    return <ForNewUserWhichIsNotColab space={space} user={user} />;
  }

  return (
    <div className="flex flex-col flex-1 w-full min-w-0">
      {(space_id && messages.length === 0) ? (
        <div className="flex flex-1 items-center justify-center w-full">
          <div className="max-w-4xl w-full px-6 text-center">
            <SparkEmptyState variant="spectra" space_id={space_id} />
          </div>
        </div>
      ) : messages.length === 0 ? (
        <div className="flex flex-1 items-center justify-center w-full">
          <div className="max-w-4xl w-full px-6 text-center">
            <SparkEmptyState variant="spark" />
          </div>
        </div>
      ) : (
        <div
          id="chat-messages"
          ref={messageContainerRef}
          className={`side-chat-scroll show-scrollbar [scrollbar-gutter:stable_both-edges] font-inter flex-1 w-full py-2 overflow-x-hidden ${
            isMessagesRendered ? "opacity-100" : "opacity-0"
          }`}
        >
          <div className="max-w-[100vw] sm:max-w-4xl w-full px-2 sm:px-6 mx-auto overflow-x-hidden">
            {messages?.map((chat, index) => {
              // Standalone system messages (no related_prompt_id) - render separately
              if (chat.role === "system" && !chat.related_prompt_id) {
                return (
                  <div
                    key={chat.id}
                    className="w-full border-b border-edge-border dark:border-edge-muted-control last:border-b-0"
                  >
                    <div className="message rounded-lg dark:text-ink-white text-ink-ink py-1 my-2 w-full break-words overflow-x-hidden font-inter text-[15px] leading-6 sm:leading-7 min-w-0">
                      <div className="whitespace-pre-wrap">
                        {chat.content}
                      </div>
                    </div>
                  </div>
                );
              }
              
              // Skip system messages with related_prompt_id - they're rendered with their prompt
              if (chat.role === "system" && chat.related_prompt_id) {
                return null;
              }
              
              // Regular user prompts with answers (and possibly system messages)
              return (
                <OneMessage
                  key={chat.id}
                  index={index}
                  chat={chat}
                  user={user}
                  isLatest={index === messages.length - 1}
                  theme={theme}
                  regenerateIndex={regenerateIndex}
                  setRegenerateIndex={setRegenerateIndex}
                  editedContent={editedIndex}
                  setEditedContent={setEditedIndex}
                  editFunctionality={editFunctionality}
                  generateStatus={generateStatus}
                  setGenerateStatus={setGenerateStatus}
                  deleteMessage={deleteMessage}
                  pendingApprovals={pendingApprovals}
                  onApprovalConfirm={onApprovalConfirm}
                  onApprovalCancel={onApprovalCancel}
                  onApprovalExpire={onApprovalExpire}
                  regenFunc={regenFunc}
                  compactAnswers={compactAnswers}
                  expandedExchangeIds={expandedExchangeIds}
                  collapsedExchangeIds={collapsedExchangeIds}
                  onExchangeExpandedChange={onExchangeExpandedChange}
                />
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default React.memo(ChatMessages);
